// docs/116 CI13: the tavern's cat and dog. The cat sleeps on the hearth rug, now and then moves to a
// windowsill or under a table; clicking her walks my band's leader over to pet her (a purr, hearts). The
// dog lies by the door and trots about; clicking him starts a game of fetch: the leader throws a stick,
// the dog bounds after it, barks, brings it back, tail going — and again, as often as you like.
// docs/121 VW1/VW2: both are the same in every view. Where they wander and when is the visit's seed and
// the World's clock (`scenery.ts`); a pet or a throw is a moment (`village_moment`) every view in the
// room plays from the module's seed, so the stick lands in the same place for everyone.
import { useEffect, useMemo, useRef, useState } from 'react';
import { playSfx } from '../../../../../core/audio';
import { smoothServerNow } from '../../../../../core/time';
import { critterArt } from '../../critters';
import { findPath, lengthOf, nearestFree, type Pt } from '../../walk';
import { catSpotOf, dogGoal, fetchOf, roundOf, walkAt, CAT_PACE, CAT_ROUND, DOG_PACE, DOG_ROUND, type CatSpot } from '../../scenery';
import { useInn } from '../store';
import { POSE, type InnMoment } from '../types';
import type { InnStage } from '../stage';
import type { Pix as PixArt } from '../paint';
import { catAsleep, catSitting, dogLying, dogSitting, dogStick, stickArt } from './art';
import { useLife } from './store';

type CatState = 'sleep' | 'walk' | 'sit' | 'pet';
type DogState = 'lie' | 'trot' | 'sit' | 'run' | 'fetch' | 'wag';

/** A moment older than this when it arrives (a reconnect's backlog) is not played. */
const STALE_MS = 15_000;
/** How long the cat sits on a sill before curling up, and a pet lasts. */
const SILL_MS = 9000, PET_MS = 3400;
/** A game of fetch, from the throw to the dog settling again. */
const WAG_MS = 14_000;

function Pix({ art, scale }: { art: PixArt; scale: number }) {
  return <span className="fk-village__pix fk-village__critter" style={{ width: art.w * scale, height: art.h * scale, backgroundImage: `url(${art.url})`, backgroundSize: '100% 100%' }} />;
}

export function Animals({ stage }: { stage: InnStage }): React.JSX.Element | null {
  const { mover, grid, plan, room, upstairs, cellPx, scale, phase } = stage;
  const live = useRef(stage);
  live.current = stage;
  const seed = stage.data.visitSeed || stage.look.seed;
  const coat = Math.floor(seed % 2);
  const [cat, setCat] = useState<CatState>('sleep');
  const [dog, setDog] = useState<DogState>('lie');
  const [stick, setStick] = useState<Pt>();
  const catRef = useRef(cat); catRef.current = cat;
  const dogRef = useRef(dog); dogRef.current = dog;
  if (!upstairs) {
    if (!mover.at('i:cat')) mover.place('i:cat', plan.cat, 1);
    if (!mover.at('i:dog')) mover.place('i:dog', plan.dog, 1);
  }
  const timers = useRef<number[]>([]);
  const later = (ms: number, fn: () => void) => { timers.current.push(window.setTimeout(fn, ms)); };
  useEffect(() => () => { for (const t of timers.current) window.clearTimeout(t); }, []);
  const walk = (k: string, to: Pt, speed: number, done?: () => void) => {
    const cur = mover.at(k);
    if (!cur) return;
    mover.walk(k, findPath(grid, cur, to), { speed, ...(done ? { done } : {}) });
  };
  const leader = () => { const h = live.current.mine.find((x) => !x.companion); return h ? { key: h.key, fig: `h:${h.key}`, id: live.current.data.payers.find((p) => p.key === h.key)?.id ?? h.id } : undefined; };

  // ---- the cat: sleeps, and once a round (the same for everyone) finds somewhere else to sleep ----
  const spots = useMemo<CatSpot[]>(() => {
    const out: CatSpot[] = [{ ...plan.cat }];
    const w = room.windows[0];
    if (w) out.push({ x: w.x - 0.05, y: w.y + w.h - 1.12, sill: true });
    const t = room.tables?.[2] ?? room.tables?.[1];
    if (t) out.push({ x: t.x + 0.3, y: t.y + 0.15 });
    return out;
  }, [plan, room]);
  const foot = (s: CatSpot): Pt => (s.sill ? nearestFree(grid, { x: s.x, y: grid.y0 }) : s);
  const catRound = useRef<number | undefined>(undefined);
  const petUntil = useRef(0);
  const catSettle = (to: CatSpot, sitFor: number) => {
    if (to.sill) mover.place('i:cat', to, 1);
    if (to.sill && sitFor > 0) { setCat('sit'); later(sitFor, () => setCat((c) => (c === 'sit' ? 'sleep' : c))); } else setCat('sleep');
  };
  const catTick = () => {
    const t = smoothServerNow() / 1000, { k, into } = roundOf(t, CAT_ROUND);
    if (catRound.current === k || performance.now() < petUntil.current) return;
    const first = catRound.current === undefined;
    catRound.current = k;
    const from = spots[catSpotOf(seed, k - 1, spots.length)]!, to = spots[catSpotOf(seed, k, spots.length)]!;
    const w = walkAt(grid, foot(from), foot(to), CAT_PACE, first ? into : 0);
    const walkMs = (lengthOf(w.line) / CAT_PACE) * 1000;
    if (from === to || w.done) {
      mover.place('i:cat', foot(to), mover.at('i:cat')?.face ?? 1);
      catSettle(to, from === to ? 0 : SILL_MS - (into * 1000 - walkMs));
      return;
    }
    mover.place('i:cat', w.at, mover.at('i:cat')?.face ?? 1);
    setCat('walk');
    mover.walk('i:cat', w.line, { from: w.along, speed: CAT_PACE, done: () => { if (to.sill) playSfx('pet.cat_comfort', { gain: 0.2 }); catSettle(to, SILL_MS); } });
  };

  // ---- the dog: lies by the door, and once a round gets up and trots somewhere ----
  const dogRound = useRef<number | undefined>(undefined);
  const fetchUntil = useRef(0);
  const dogTick = () => {
    const t = smoothServerNow() / 1000, { k, into } = roundOf(t, DOG_ROUND);
    if (dogRound.current === k || performance.now() < fetchUntil.current) return;
    const first = dogRound.current === undefined;
    dogRound.current = k;
    const goal = dogGoal(seed, k, plan.dog, grid, room.w);
    const from = first ? dogGoal(seed, k - 1, plan.dog, grid, room.w).to : mover.at('i:dog') ?? plan.dog;
    const w = walkAt(grid, from, goal.to, DOG_PACE, first ? into : 0);
    if (w.done) { mover.place('i:dog', goal.to, mover.at('i:dog')?.face ?? 1); setDog(goal.home ? 'lie' : 'sit'); return; }
    mover.place('i:dog', w.at, mover.at('i:dog')?.face ?? 1);
    setDog('trot');
    mover.walk('i:dog', w.line, { from: w.along, speed: DOG_PACE, done: () => { setDog(goal.home ? 'lie' : 'sit'); if (goal.bark) playSfx('inn.bark', { gain: 0.25 }); } });
  };

  useEffect(() => {
    if (upstairs || stage.reduced) return;
    // the visit's seed may arrive after the first frame: work the rounds out afresh from it
    catRound.current = undefined; dogRound.current = undefined;
    catTick(); dogTick();
    const iv = window.setInterval(() => { catTick(); dogTick(); }, 1000);
    return () => window.clearInterval(iv);
  }, [upstairs, seed]);

  // ---- my clicks: walk my leader over, then tell every view (the moment comes back to me too) ----
  const pet = () => {
    const L = leader();
    const c = mover.at('i:cat');
    if (!L || L.id === undefined || !c || catRef.current === 'walk') return;
    const st = c.y < room.floor + 0.5 ? nearestFree(grid, { x: c.x - 0.6, y: grid.y0 }) : nearestFree(grid, { x: c.x - 0.85, y: c.y });
    useInn.getState().act([L.key], undefined);
    live.current.walkBand([{ ...st }], POSE.standing, () => {
      const me = mover.at(L.fig);
      if (me) void live.current.data.act.moment('pet', L.id!, me.x, me.y);
    });
  };
  const fetch = () => {
    const L = leader();
    const d = mover.at('i:dog');
    if (!L || L.id === undefined || !d || dogRef.current === 'run' || dogRef.current === 'fetch') return;
    const me0 = mover.at(L.fig);
    const near = me0 && Math.hypot(me0.x - d.x, me0.y - d.y) < 2.2;
    const tell = () => { const me = mover.at(L.fig); if (me) void live.current.data.act.moment('fetch', L.id!, me.x, me.y); };
    playSfx('inn.bark', { gain: 0.35 });
    if (near) tell(); else live.current.walkBand([nearestFree(grid, { x: d.x + (d.x < room.w / 2 ? 1 : -1), y: d.y })], POSE.standing, tell);
  };

  // ---- the moments, as every view plays them ----
  const playPet = (m: InnMoment) => {
    const cc = mover.at('i:cat');
    if (!cc) return;
    petUntil.current = performance.now() + PET_MS + 200;
    const fig = `h:${m.heroKey}`;
    if (m.mine) { const me = mover.at(fig); if (me) mover.face(fig, cc.x >= me.x ? 1 : -1); useInn.getState().flash([m.heroKey], 'pet', 3200); }
    setCat('pet');
    playSfx('inn.purr', { gain: m.mine ? 0.8 : 0.5 });
    const life = useLife.getState();
    life.burst('hearts', cc.x + 0.45, cc.y + 0.2, 2400);
    later(1300, () => life.burst('hearts', cc.x + 0.55, cc.y + 0.1, 2400));
    later(PET_MS, () => setCat((x) => (x === 'pet' ? (cc.y < room.floor + 0.5 ? 'sit' : 'sleep') : x)));
  };
  const playFetch = (m: InnMoment) => {
    if (!mover.at('i:dog')) return;
    const from = { x: m.x, y: m.y };
    const f = fetchOf(m.seed, from, grid, room.w);
    const fig = `h:${m.heroKey}`;
    fetchUntil.current = performance.now() + 60_000;
    mover.stop('i:dog');
    setStick(undefined);
    setDog('sit');
    if (!m.mine) playSfx('inn.bark', { gain: 0.3 });
    if (m.mine) { mover.face(fig, f.dir); useInn.getState().flash([m.heroKey], 'throw', 900); }
    const life = useLife.getState();
    later(380, () => {
      playSfx('inn.stick', { gain: m.mine ? 0.7 : 0.5 });
      const to = { x: f.land.x + 0.3, y: f.land.y + 0.7 };
      const id = life.fly({ kind: 'stick', from: { x: from.x + 0.4, y: from.y + 0.3 }, to, ms: f.flyMs, arc: 1.6 });
      later(f.flyMs, () => { life.land(id); setStick(to); });
      later(250, () => {
        setDog('run');
        walk('i:dog', f.land, 4.2, () => {
          setStick(undefined);
          setDog('fetch');
          playSfx('inn.bark', { gain: 0.6 });
          const back = nearestFree(grid, { x: from.x + f.dir * 0.9, y: from.y + 0.2 });
          later(350, () => walk('i:dog', back, 3.2, () => {
            mover.face('i:dog', -f.dir as 1 | -1);
            setDog('wag');
            setStick({ x: back.x + (f.dir < 0 ? 0.9 : -0.1), y: back.y + 0.85 });
            if (f.praise) useLife.getState().say(fig, f.praise, 1800);
            fetchUntil.current = performance.now() + WAG_MS;
            later(WAG_MS, () => { setStick(undefined); setDog((x) => (x === 'wag' ? 'sit' : x)); });
          }));
        });
      });
    });
  };
  const played = useRef<Set<number> | undefined>(undefined);
  const momentsSig = stage.data.moments.map((m) => m.n).join();
  useEffect(() => {
    if (upstairs) return;
    if (!played.current) played.current = new Set();
    for (const m of stage.data.moments) {
      if (played.current.has(m.n)) continue;
      played.current.add(m.n);
      if (Math.abs(smoothServerNow() - m.atMs) > STALE_MS) continue;
      if (m.kind === 'pet') playPet(m);
      else playFetch(m);
    }
  }, [momentsSig, upstairs]);

  if (upstairs) return null;
  const at = (k: string) => { const p = mover.at(k)!; return { left: Math.round(p.x * cellPx), top: Math.round(p.y * cellPx), zIndex: Math.round(p.y * 10) + 1 }; };
  const cs = scale + 1;
  const catArt = cat === 'walk' ? critterArt('cat', coat, phase) : cat === 'sleep' ? catAsleep(coat) : catSitting(coat);
  const dogArt = dog === 'trot' || dog === 'run' ? critterArt('dog', coat, phase) : dog === 'fetch' ? dogStick(coat, phase) : dog === 'lie' ? dogLying(coat, phase) : dogSitting(coat, dog === 'wag' ? phase : 0);
  return (
    <>
      <button type="button" ref={mover.reg('i:cat')} className={`fk-hit fk-village__figure fk-inn__animal fk-inn__animal--cat fk-inn__animal--${cat}`} data-testid="inn-cat" data-state={cat} style={at('i:cat')} data-face={mover.at('i:cat')!.face < 0 ? 'left' : 'right'}
        aria-label="The tavern cat: pet her" onClick={pet}>
        <span className="fk-village__shadow" />
        <span className="fk-village__face"><Pix art={catArt} scale={cs} /></span>
        <span className="fk-village__plate fk-inn__gplate"><span className="fk-village__plate-name">Marmalade</span><span className="fk-village__plate-sub">the tavern cat · pet her</span></span>
      </button>
      <button type="button" ref={mover.reg('i:dog')} className={`fk-hit fk-village__figure fk-inn__animal fk-inn__animal--dog fk-inn__animal--${dog}`} data-testid="inn-dog" data-state={dog} style={at('i:dog')} data-face={mover.at('i:dog')!.face < 0 ? 'left' : 'right'}
        aria-label="The tavern dog: play fetch" onClick={fetch}>
        <span className="fk-village__shadow" />
        <span className="fk-village__face"><Pix art={dogArt} scale={cs} /></span>
        <span className="fk-village__plate fk-inn__gplate"><span className="fk-village__plate-name">Biscuit</span><span className="fk-village__plate-sub">the tavern dog · play fetch</span></span>
      </button>
      {stick && <span className="fk-inn__stick" data-testid="inn-stick" data-x={stick.x.toFixed(2)} data-y={stick.y.toFixed(2)} style={{ left: Math.round(stick.x * cellPx), top: Math.round(stick.y * cellPx), zIndex: Math.round(stick.y * 10) }} aria-hidden="true"><span className="fk-village__pix" style={{ width: stickArt().w * scale, height: stickArt().h * scale, backgroundImage: `url(${stickArt().url})`, backgroundSize: '100% 100%' }} /></span>}
    </>
  );
}
